import React, { useState, useEffect, useMemo } from 'react';
import Box from '@mui/material/Box';
import {
  Card,
  CardContent,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Typography,
  Grid,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { DataGrid } from '@mui/x-data-grid';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Page, Text, View, Document, PDFViewer } from '@react-pdf/renderer';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DescriptionIcon from '@mui/icons-material/Description';
import SearchIcon from '@mui/icons-material/Search';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CancelIcon from '@mui/icons-material/Cancel';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PercentIcon from '@mui/icons-material/Percent';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import { useMaterialUIController } from "context";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpDataGridSx, StatusPill } from "components/financepro";
import { useApi } from "lib/api";

function StatCard({ label, value, icon: Icon, accent }) {
  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        borderRadius: 2.5,
        border: `1px solid ${FP.color.border}`,
        bgcolor: FP.color.cardBg,
        boxShadow: FP.shadow.soft,
        overflow: 'hidden',
        position: 'relative',
        transition: 'all 0.2s ease',
        '&:hover': { boxShadow: FP.shadow.md, transform: 'translateY(-2px)' },
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          bgcolor: accent,
        },
      }}
    >
      <CardContent sx={{ p: 2.4, '&:last-child': { pb: 2.4 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1.5}>
          <Box flex={1} minWidth={0}>
            <Typography
              sx={{
                color: FP.color.textSecondary,
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                fontSize: '0.66rem',
                mb: 1,
              }}
            >
              {label}
            </Typography>
            <Typography
              sx={{
                fontWeight: 800,
                color: FP.color.textPrimary,
                fontVariantNumeric: 'tabular-nums',
                lineHeight: 1.2,
                letterSpacing: '-0.02em',
                fontSize: '1.4rem',
              }}
            >
              {value}
            </Typography>
          </Box>
          <Box
            sx={{
              flexShrink: 0,
              width: 42,
              height: 42,
              borderRadius: 1.4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: `${accent}14`,
              color: accent,
            }}
          >
            <Icon sx={{ fontSize: 20 }} />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

const INPUT_SX = {
  '& .MuiOutlinedInput-root': {
    height: 46,
    minHeight: 46,
    borderRadius: 1.6,
    bgcolor: '#FFFFFF',
    '& fieldset': { borderColor: FP.color.border },
    '&:hover fieldset': { borderColor: FP.color.borderStrong },
    '&.Mui-focused fieldset': { borderColor: FP.color.navy },
  },
  '& .MuiInputLabel-root': { fontSize: '0.86rem' },
};

function InformePrestamo() {
  const [presentarpdf, setPresentarpdf] = useState(false)
  const [fecha1, setFecha1] = useState("")
  const [fecha2, setFecha2] = useState("")
  const [nosucursal, setNosucursal] = useState(null)
  const [sucursal, setSucursal] = useState([])
  const [rows, setRows] = useState([])
  const [filtroStatus, setFiltroStatus] = useState('todos')
  const [controller] = useMaterialUIController()
  const { nombreNegocio } = controller
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    post('/api/recuperasucursales', { fechadesde: fecha1, fechahasta: fecha2, sucursal: nosucursal })
      .then(function ({ status, data }) {
        if (status === 200) {
          setSucursal(data["sucursales"])
        }
        if (status === 400) {
          setRows([])
          showAlert({ message: data["Error"], color: "error", autoHideDuration: 3000 })
        }
      })
  }, []);

  const columns = useMemo(
    () => [
      { field: 'id', headerName: 'DOC #', width: 80, minWidth: 70, align: 'center', headerAlign: 'center' },
      { field: 'fecha', headerName: 'Fecha', type: 'string', minWidth: 100, flex: 0.6, editable: true },
      { field: 'nombres', headerName: 'Nombre', type: 'string', minWidth: 130, flex: 1 },
      { field: 'cedula', headerName: 'Cédula', type: 'string', flex: 1, minWidth: 120, editable: true },
      { field: 'cantsol', headerName: 'Cantidad', type: 'string', minWidth: 110, flex: 0.7 },
      { field: 'plazo', headerName: 'Plazo', type: 'string', minWidth: 90, flex: 0.5 },
      { field: 'interes', headerName: 'Interés', type: 'string', minWidth: 90, flex: 0.5 },
      { field: 'balcap_pend', headerName: 'Cap. pend.', type: 'string', minWidth: 110, flex: 0.55 },
      { field: 'balint_pend', headerName: 'Int. pend.', type: 'string', minWidth: 110, flex: 0.55 },
      {
        field: 'status',
        headerName: 'Status',
        type: 'string',
        minWidth: 110,
        flex: 0.5,
        renderCell: (params) => {
          const s = String(params.value ?? '').trim().toUpperCase();
          if (s === 'A') return <StatusPill label="Activo" variant="success" />;
          if (s === 'C') return <StatusPill label="Cancelado" variant="neutral" />;
          return <StatusPill label={params.value || '—'} variant="info" />;
        },
      },
      {
        field: 'g',
        headerName: '',
        width: 56,
        minWidth: 56,
        maxWidth: 56,
        sortable: false,
        disableColumnMenu: true,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <ContentCopyIcon
            sx={{ cursor: 'pointer', color: FP.color.textSecondary, '&:hover': { color: FP.color.navy } }}
            onClick={() =>
              copyTextToClipboard(
                'https:motoprestamo.suitorbit.com/expediente/' +
                localStorage.getItem('id') + '/' + params.row.cedula
              )
            }
          />
        ),
      },
    ],
    []
  );

  const filasVista = useMemo(() => {
    if (filtroStatus === 'todos') return rows;
    return rows.filter((r) => {
      const s = String(r.status ?? '').trim().toUpperCase();
      if (filtroStatus === 'activos') return s === 'A';
      if (filtroStatus === 'cancelados') return s === 'C';
      return true;
    });
  }, [rows, filtroStatus]);

  function copyTextToClipboard(text) {
    if ('clipboard' in navigator) {
      return navigator.clipboard.writeText(text);
    } else {
      return document.execCommand('copy', true, text);
    }
  }

  function procesarrequerimiento(event) {
    if (event && typeof event.preventDefault === 'function') {
      event.preventDefault();
    }
    post('/api/informesprestamos', { fechadesde: fecha1, fechahasta: fecha2, sucursal: nosucursal })
      .then(function ({ status, data }) {
        if (status === 200) {
          setRows(data["prestamo"])
        }
        if (status === 400) {
          setRows([])
          showAlert({ message: data["Error"], color: "error", autoHideDuration: 3000 })
        }
      })
  }

  const MyDocument = () => {
    const parseN = (v) => parseFloat(String(v ?? '0').split(',').join('')) || 0;
    const tvalor = filasVista.reduce((acc, filas) => acc + parseN(filas.cantsol), 0);
    const tinteres = filasVista.reduce((acc, filas) => acc + parseN(filas.interes), 0);
    return (
      <PDFViewer style={{ height: isXs ? "calc(100vh - 120px)" : "550px", width: "100%" }}>
        <Document>
          <Page size="A4">
            <View style={{ textAlign: "center", width: "94%", fontSize: 14, textTransform: "uppercase", marginTop: "15px" }} fixed>
              <Text >{nombreNegocio}</Text>
            </View>
            <View style={{ textAlign: "center", width: "94%", fontSize: 9, textTransform: "uppercase" }} fixed>
              <Text >Reporte de Motores</Text>
            </View>
            <View style={{ textAlign: "center", width: "94%", fontSize: 9, textTransform: "uppercase" }} fixed>
              <Text>  </Text>
            </View>
            <View style={{ display: "flex", alignItems: "center", flexDirection: "row", justifyContent: "left", width: "94%", height: "25px", fontSize: 10, backgroundColor: "gray", color: "black", marginLeft: "15px" }} fixed>
              <Text style={{ width: "100px", justifyContent: "left" }} fixed>Prest#</Text>
              <Text style={{ width: "100px", justifyContent: "left" }} fixed>Fecha</Text>
              <Text style={{ width: "100px", justifyContent: "left" }} fixed>Nombres</Text>
              <Text style={{ width: "100px", justifyContent: "left" }} fixed>Apellidos</Text>
              <Text style={{ width: "100px", justifyContent: "left" }} fixed>Nombre</Text>
              <Text style={{ width: "100px", justifyContent: "left" }} fixed>Cantidad</Text>
              <Text style={{ width: "100px", justifyContent: "left" }} fixed>Plazo</Text>
              <Text style={{ width: "100px", justifyContent: "left" }} fixed>Interes</Text>
              <Text style={{ width: "100px", justifyContent: "left" }} fixed>Status</Text>
            </View>
            {filasVista.map((filas, i) =>
              <View key={i} style={{ display: "flex", alignItems: "center", flexDirection: "row", justifyContent: "left", width: "94%", height: "15px", fontSize: 10, color: "black", marginLeft: "15px" }} >
                <Text style={{ width: "100px", justifyContent: "left" }} fixed>{filas.id}</Text>
                <Text style={{ width: "100px", justifyContent: "left" }} fixed>{filas.fecha}</Text>
                <Text style={{ width: "100px", justifyContent: "left" }} fixed>{filas.nombres}</Text>
                <Text style={{ width: "100px", justifyContent: "left" }} fixed>{filas.apellidos}</Text>
                <Text style={{ width: "100px", justifyContent: "left" }} fixed>{filas.nombrecompleto}</Text>
                <Text style={{ width: "100px", justifyContent: "left" }} fixed>{filas.cantsol}</Text>
                <Text style={{ width: "100px", justifyContent: "left" }} fixed>{filas.plazo}</Text>
                <Text style={{ width: "100px", justifyContent: "left" }} fixed>{filas.interes}</Text>
                <Text style={{ width: "100px", justifyContent: "left" }} fixed>{filas.status}</Text>
              </View>
            )}
            <Text> </Text>
            <View style={{ display: "flex", alignItems: "center", flexDirection: "row", justifyContent: "left", width: "94%", height: "25px", fontSize: 10, backgroundColor: "white", color: "black", marginLeft: "15px" }} >
              <Text style={{ width: "100px", justifyContent: "left" }} fixed>Totales</Text>
              <Text style={{ width: "100px", justifyContent: "left" }} fixed> </Text>
              <Text style={{ width: "100px", justifyContent: "left" }} fixed> </Text>
              <Text style={{ width: "100px", justifyContent: "left" }} fixed> </Text>
              <Text style={{ width: "100px", justifyContent: "left" }} fixed> </Text>
              <Text style={{ width: "100px", justifyContent: "left" }} fixed>{tvalor.toFixed(2)}</Text>
              <Text style={{ width: "100px", justifyContent: "left" }} fixed> </Text>
              <Text style={{ width: "100px", justifyContent: "left" }} fixed>{tinteres.toFixed(2)} </Text>
              <Text style={{ width: "100px", justifyContent: "left" }} fixed> </Text>
            </View>
          </Page>
        </Document>
      </PDFViewer>
    );
  };

  function handlepdf() {
    if (filasVista.length == 0) {
      showAlert({ message: "No hay datos para procesar este reporte", color: "error", autoHideDuration: 3000 })
    } else {
      setPresentarpdf(true)
    }
  }

  const totalCantidad = filasVista.reduce((acc, item) => acc + (parseFloat(String(item.cantsol || "0").split(',').join('')) || 0), 0);
  const totalBalCapPend = filasVista.reduce((acc, item) => acc + (parseFloat(String(item.balcap_pend ?? "0").split(',').join('')) || 0), 0);
  const totalBalIntPend = filasVista.reduce((acc, item) => acc + (parseFloat(String(item.balint_pend ?? "0").split(',').join('')) || 0), 0);

  return (
    <Box
      component="main"
      sx={{
        minHeight: '100vh',
        bgcolor: FP.color.surface,
      }}
    >
      <PageHeader
        title="Préstamos"
        subtitle="Informe de Préstamos"
        cta={
          <Button
            variant="contained"
            startIcon={<FileDownloadRoundedIcon />}
            disabled={filasVista.length === 0}
            onClick={handlepdf}
            sx={{
              bgcolor: FP.color.navy,
              color: '#FFFFFF',
              borderRadius: 1.4,
              px: 2.2,
              height: 38,
              boxShadow: 'none',
              textTransform: 'none',
              fontWeight: 700,
              '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
              '&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#94A3B8' },
            }}
          >
            Export Data
          </Button>
        }
      />

      <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
        <Card
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            bgcolor: FP.color.cardBg,
            boxShadow: FP.shadow.soft,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: '#FAFBFE',
              display: 'flex',
              alignItems: 'center',
              gap: 1.2,
            }}
          >
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <SearchIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Filtros de Búsqueda
            </Typography>
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  type="date"
                  id="fecha-desde"
                  label="Fecha Desde"
                  value={fecha1}
                  onChange={(event) => setFecha1(event.target.value)}
                  variant="outlined"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={INPUT_SX}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  type="date"
                  id="fecha-hasta"
                  label="Fecha Hasta"
                  value={fecha2}
                  onChange={(event) => setFecha2(event.target.value)}
                  variant="outlined"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={INPUT_SX}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  disablePortal
                  id="sucursal-autocomplete"
                  options={sucursal || []}
                  value={nosucursal}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    return option?.label || '';
                  }}
                  isOptionEqualToValue={(option, value) => {
                    const optionLabel = typeof option === 'string' ? option : option?.label;
                    const valueLabel = typeof value === 'string' ? value : value?.label;
                    return optionLabel === valueLabel;
                  }}
                  onChange={(event, value) => setNosucursal(value || null)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Sucursal"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      sx={INPUT_SX}
                    />
                  )}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth variant="outlined" sx={INPUT_SX}>
                  <InputLabel id="filtro-status-label" shrink>Estado del préstamo</InputLabel>
                  <Select
                    labelId="filtro-status-label"
                    id="filtro-status"
                    label="Estado del préstamo"
                    notched
                    value={filtroStatus}
                    onChange={(e) => setFiltroStatus(e.target.value)}
                  >
                    <MenuItem value="todos">Todos</MenuItem>
                    <MenuItem value="activos">Activos</MenuItem>
                    <MenuItem value="cancelados">Cancelados</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Box display="flex" justifyContent="flex-end" sx={{ mt: 2.5 }}>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={() => procesarrequerimiento()}
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                  minWidth: { sm: 200 },
                  height: 46,
                  bgcolor: FP.color.navy,
                  color: '#FFFFFF',
                  borderRadius: 1.4,
                  textTransform: 'none',
                  fontSize: '0.94rem',
                  fontWeight: 700,
                  boxShadow: 'none',
                  '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                }}
              >
                Procesar
              </Button>
            </Box>
          </CardContent>
        </Card>

        {rows.length > 0 && (
          <Box
            sx={{
              display: 'grid',
              width: '100%',
              gap: 2,
              mb: 3,
              gridTemplateColumns: {
                xs: 'minmax(0, 1fr)',
                sm: 'repeat(2, minmax(0, 1fr))',
                md: 'repeat(4, minmax(0, 1fr))',
              },
            }}
          >
            <StatCard
              label="Registros (vista)"
              value={String(filasVista.length)}
              icon={DescriptionIcon}
              accent={FP.color.accentBlue}
            />
            <StatCard
              label="Total cantidad"
              value={`$${totalCantidad.toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              icon={AttachMoneyIcon}
              accent={FP.color.navy}
            />
            <StatCard
              label="Capital pendiente"
              value={`$${totalBalCapPend.toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              icon={AccountBalanceIcon}
              accent={FP.color.successDark}
            />
            <StatCard
              label="Interés pendiente"
              value={`$${totalBalIntPend.toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              icon={PercentIcon}
              accent={FP.color.warning}
            />
          </Box>
        )}

        <Card
          elevation={0}
          sx={{
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            bgcolor: FP.color.cardBg,
            boxShadow: FP.shadow.soft,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: '#FAFBFE',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Listado de Préstamos
            </Typography>
            {filasVista.length > 0 && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<PictureAsPdfIcon />}
                onClick={() => handlepdf()}
                sx={{
                  borderColor: FP.color.danger,
                  color: FP.color.danger,
                  borderRadius: 1.2,
                  textTransform: 'none',
                  fontWeight: 700,
                  '&:hover': { bgcolor: FP.color.dangerSoft, borderColor: FP.color.danger },
                }}
              >
                Generar PDF
              </Button>
            )}
          </Box>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
            {rows.length > 0 ? (
              filasVista.length > 0 ? (
                <>
                  <Box sx={{ display: { xs: 'none', md: 'block' }, width: '100%', overflowX: 'auto' }}>
                    <DataGrid
                      rows={filasVista}
                      columns={columns}
                      getRowId={(row) => (row.id != null ? String(row.id) : `${row.cedula}-${row.fecha}`)}
                      pageSizeOptions={[5, 10, 25, 50]}
                      initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                      checkboxSelection
                      disableRowSelectionOnClick
                      rowHeight={42}
                      sx={{ minWidth: { xs: 720, sm: '100%' }, height: 520, ...fpDataGridSx }}
                      autoHeight={false}
                    />
                  </Box>
                  <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1.4 }}>
                    {filasVista.map((row, idx) => {
                      const s = String(row.status ?? '').trim().toUpperCase();
                      const pill = s === 'A'
                        ? <StatusPill label="Activo" variant="success" />
                        : s === 'C'
                          ? <StatusPill label="Cancelado" variant="neutral" />
                          : <StatusPill label={row.status || '—'} variant="info" />;
                      return (
                        <Box
                          key={row.id != null ? String(row.id) : `${row.cedula}-${row.fecha}-${idx}`}
                          sx={{
                            border: `1px solid ${FP.color.border}`,
                            borderRadius: 2,
                            p: 1.5,
                            bgcolor: '#FFFFFF',
                            boxShadow: FP.shadow.soft,
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary, fontSize: '1rem', lineHeight: 1.2 }}>
                                Préstamo #{row.id}
                              </Typography>
                              <Typography sx={{ color: FP.color.textPrimary, fontSize: '0.85rem', fontWeight: 600, mt: 0.25, wordBreak: 'break-word' }}>
                                {[row.nombres, row.apellidos].filter(Boolean).join(' ') || '—'}
                              </Typography>
                              <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.78rem', mt: 0.1 }}>
                                {row.fecha} · Cédula {row.cedula}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                              {pill}
                              <ContentCopyIcon
                                sx={{ cursor: 'pointer', color: FP.color.textSecondary, fontSize: 18, ml: 0.5, '&:hover': { color: FP.color.navy } }}
                                onClick={() =>
                                  copyTextToClipboard(
                                    'https:motoprestamo.suitorbit.com/expediente/' +
                                    localStorage.getItem('id') + '/' + row.cedula
                                  )
                                }
                              />
                            </Box>
                          </Box>
                          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 1 }}>
                            {[
                              ['Cantidad', row.cantsol],
                              ['Plazo', row.plazo],
                              ['Interés', row.interes],
                              ['Cap. pend.', row.balcap_pend],
                              ['Int. pend.', row.balint_pend],
                            ].map(([label, value]) => (
                              <Box key={label}>
                                <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                                  {label}
                                </Typography>
                                <Typography sx={{ color: FP.color.textPrimary, fontSize: '0.92rem', fontWeight: 600, fontVariantNumeric: 'tabular-nums', wordBreak: 'break-word' }}>
                                  {value ?? '—'}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </>
              ) : (
                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                  <DescriptionIcon sx={{ fontSize: 56, color: FP.color.textMuted, mb: 2 }} />
                  <Typography sx={{ color: FP.color.textPrimary, fontWeight: 700 }} gutterBottom>
                    Ningún préstamo coincide con el estado seleccionado
                  </Typography>
                  <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.9rem' }} textAlign="center" px={2}>
                    Cambie &quot;Estado del préstamo&quot; o procese de nuevo con otro rango.
                  </Typography>
                </Box>
              )
            ) : (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                <DescriptionIcon sx={{ fontSize: 56, color: FP.color.textMuted, mb: 2 }} />
                <Typography sx={{ color: FP.color.textPrimary, fontWeight: 700 }} gutterBottom>
                  No hay datos para mostrar
                </Typography>
                <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.9rem' }} textAlign="center" px={2}>
                  Seleccione los filtros y haga clic en &quot;Procesar&quot; para obtener resultados.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {presentarpdf === true && (
          <Dialog
            open={presentarpdf}
            maxWidth="lg"
            fullWidth={true}
            fullScreen={isXs}
            onClose={() => setPresentarpdf(false)}
            PaperProps={{ sx: { borderRadius: { xs: 0, sm: 2 } } }}
          >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" fontWeight="bold">
                Vista Previa del Reporte PDF
              </Typography>
              <Button onClick={() => setPresentarpdf(false)} sx={{ minWidth: 'auto', p: 1 }}>
                <CancelIcon />
              </Button>
            </DialogTitle>
            <DialogContent>
              <MyDocument />
            </DialogContent>
          </Dialog>
        )}
      </Box>
    </Box>
  );
}
export default InformePrestamo
